/**
 * Locate an Agent/Proofread snippet inside a live Word paragraph so hybrid apply
 * can splice a full-paragraph target.
 *
 * `resolveParagraphForApply` matches with `normalizeForMatch` (collapses
 * whitespace, strips Word auto-number prefixes like `1)`). The old compose path
 * required an exact `indexOf`, so Apply was flaky: search found the paragraph,
 * then splice aborted because the snippet still had the list marker / different
 * spaces / RTL marks.
 */

/** Same leading auto-number strip as DocumentReader.normalizeForMatch. */
const LEADING_LIST_MARKER =
  /^[\s\u200E\u200F\u061C]*\d+(?:\.\d+){0,4}[.)]?[\s\u00A0\u200E\u200F\u061C]+/;

const INVISIBLE_OR_BIDI = /[\u200B-\u200F\u061C\uFEFF\u00AD]/;

export type LocatedSnippet = {
  start: number;
  end: number;
  /** True when the snippet's leading `1)` / `4.4.` was not present in the paragraph. */
  strippedListMarker: boolean;
};

export function stripLeadingListMarker(text: string): string {
  return (text || '').replace(LEADING_LIST_MARKER, '');
}

function isIgnorableChar(ch: string): boolean {
  return INVISIBLE_OR_BIDI.test(ch);
}

function canonicalChar(ch: string): string {
  if (ch === '\u2018' || ch === '\u2019' || ch === '\u2032') return "'";
  if (ch === '\u201C' || ch === '\u201D' || ch === '\u2033') return '"';
  if (ch === '\u2013' || ch === '\u2014') return '-';
  if (/\s/.test(ch) || ch === '\u00A0') return ' ';
  return ch;
}

function collapseWithOrigMap(text: string): { collapsed: string; map: number[] } {
  const collapsed: string[] = [];
  const map: number[] = [];
  let lastWasSpace = true;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (isIgnorableChar(ch)) continue;
    const canon = canonicalChar(ch);
    if (canon === ' ') {
      if (lastWasSpace) continue;
      lastWasSpace = true;
      collapsed.push(' ');
      map.push(i);
      continue;
    }
    lastWasSpace = false;
    collapsed.push(canon);
    map.push(i);
  }
  if (collapsed.length > 0 && collapsed[collapsed.length - 1] === ' ') {
    collapsed.pop();
    map.pop();
  }
  return { collapsed: collapsed.join(''), map };
}

function locateCollapsed(
  baseline: string,
  needle: string,
  strippedListMarker: boolean,
): LocatedSnippet | null {
  const trimmedNeedle = needle.trim();
  if (trimmedNeedle.length < 8) return null;
  const { collapsed, map } = collapseWithOrigMap(baseline);
  const { collapsed: needleCollapsed } = collapseWithOrigMap(trimmedNeedle);
  if (!needleCollapsed) return null;
  const pos = collapsed.indexOf(needleCollapsed);
  if (pos === -1) return null;
  const last = pos + needleCollapsed.length - 1;
  if (last >= map.length) return null;
  const start = map[pos];
  let end = map[last] + 1;
  while (end < baseline.length && isIgnorableChar(baseline[end])) {
    end += 1;
  }
  return { start, end, strippedListMarker };
}

function whitespaceFlexibleRange(baseline: string, snippet: string): LocatedSnippet | null {
  const trimmed = snippet.trim();
  if (!trimmed) return null;
  const pattern = trimmed
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\s+/g, '\\s+');
  if (!pattern) return null;
  try {
    const match = new RegExp(pattern).exec(baseline);
    if (!match) return null;
    return { start: match.index, end: match.index + match[0].length, strippedListMarker: false };
  } catch {
    return null;
  }
}

export function locateSnippetInParagraph(baseline: string, snippet: string): LocatedSnippet | null {
  if (!baseline || !snippet) return null;

  const exactIdx = baseline.indexOf(snippet);
  if (exactIdx !== -1) {
    return { start: exactIdx, end: exactIdx + snippet.length, strippedListMarker: false };
  }

  const trimmed = snippet.trim();
  if (trimmed && trimmed !== snippet) {
    const trimIdx = baseline.indexOf(trimmed);
    if (trimIdx !== -1) {
      return { start: trimIdx, end: trimIdx + trimmed.length, strippedListMarker: false };
    }
  }

  const ws = whitespaceFlexibleRange(baseline, trimmed || snippet);
  if (ws) return ws;

  const collapsed = locateCollapsed(baseline, trimmed || snippet, false);
  if (collapsed) return collapsed;

  const stripped = stripLeadingListMarker(trimmed || snippet);
  if (stripped && stripped !== (trimmed || snippet)) {
    const strippedExact = baseline.indexOf(stripped);
    if (strippedExact !== -1) {
      return {
        start: strippedExact,
        end: strippedExact + stripped.length,
        strippedListMarker: true,
      };
    }
    const strippedWs = whitespaceFlexibleRange(baseline, stripped);
    if (strippedWs) return { ...strippedWs, strippedListMarker: true };
    const strippedCollapsed = locateCollapsed(baseline, stripped, true);
    if (strippedCollapsed) return strippedCollapsed;
  }

  return null;
}

/**
 * Replace the located snippet in `baseline` with `suggestion`.
 * When the live paragraph is auto-numbered (no `1)` in text) and the snippet
 * still carries that marker, strip it from the suggestion too so we do not
 * insert a duplicate number.
 */
export function spliceSnippetIntoParagraph(
  baseline: string,
  snippetOriginal: string,
  snippetSuggestion: string,
): string | null {
  const located = locateSnippetInParagraph(baseline, snippetOriginal);
  if (!located) return null;
  const replacement = located.strippedListMarker
    ? stripLeadingListMarker(snippetSuggestion)
    : snippetSuggestion;
  return baseline.slice(0, located.start) + replacement + baseline.slice(located.end);
}
